/**
 * GraphInstance - Runtime implementation of IGraphInstance
 *
 * Implements INode interface so graph instances can be treated as nodes.
 * Virtual inputs/outputs proxy to internal nodes via the definition's api.
 */

import type { IGraphInstance } from './IGraphInstance';
import { type GraphDefinition, getNodeType } from './GraphDefinition';
import type { InstanceConnection } from './Connection';
import type { INode } from '../nodes/INode';
import { LogLevel } from '../nodes/INode';
import type { IRuntime } from '../runtime/IRuntime';
import type { IEndpoint } from '../runtime/IEndpoint';
import type { Payload } from './Payload';
import { logger } from '../logging';


export class GraphInstance implements IGraphInstance, INode {
  // INode properties
  readonly id: string;              // Local id within parent (e.g., 'mysynth1')
  readonly type: string;
  endpoints: Record<string, IEndpoint> = {};
  logLevel: LogLevel = LogLevel.Error;

  // GraphInstance-specific
  readonly instancePath: string;    // Full path for lookups (e.g., 'root/mysynth1')
  definitionId: string;
  nodes: Record<string, INode> = {};
  connections: InstanceConnection[] = [];

  // Private
  private runtime: IRuntime;

  /**
   * Get the current definition from Runtime.
   * Always looks up dynamically to avoid stale references when definitions are replaced.
   */
  private get definition(): GraphDefinition {
    const def = this.runtime.getGraphDefinition(this.definitionId);
    if (!def) {
      throw new Error(`Definition '${this.definitionId}' not found in runtime`);
    }
    return def;
  }

  constructor(
    nodeId: string,
    instancePath: string,
    definition: GraphDefinition,
    runtime: IRuntime
  ) {
    this.id = nodeId;
    this.instancePath = instancePath;
    this.type = definition.id;
    this.definitionId = definition.id;
    this.runtime = runtime;
  }

  /**
   * Resolve an api path to the actual primitive node and endpoint.
   * Handles sub-graphs by recursively resolving through their api.
   * Returns { nodeId, endpointId } where nodeId is the flattened path to a primitive.
   */
  private resolveApiPath(path: string): { nodeId: string; endpointId: string } | null {
    const [nodeId, endpointId] = path.split('.');

    // Check if the node is directly in our nodes (primitive)
    if (this.nodes[nodeId]) {
      return { nodeId, endpointId };
    }

    // Node not found - check if it's a sub-graph
    const nodeEntry = this.definition.nodes[nodeId];
    if (!nodeEntry) return null;
    const nodeType = getNodeType(nodeEntry);

    const subGraphDef = this.runtime.getGraphDefinition(nodeType);
    if (!subGraphDef) return null;

    // It's a sub-graph - resolve through its api
    const subApiEntry = subGraphDef.api[endpointId];
    if (!subApiEntry) return null;

    const subExposedEndpoint = subApiEntry;
    const [subNodeId, subEndpointId] = subExposedEndpoint.path.split('.');

    // Return the flattened path: nodeId.subNodeId
    const flattenedNodeId = `${nodeId}.${subNodeId}`;
    return { nodeId: flattenedNodeId, endpointId: subEndpointId };
  }

  /**
   * Get a node by potentially nested path (e.g., "subgraph.node").
   * Traverses through subgraph instances to find the target node.
   */
  private getNodeByPath(nodePath: string): INode | null {
    const parts = nodePath.split('.');

    // Single part - direct child
    if (parts.length === 1) {
      return this.nodes[parts[0]] ?? null;
    }


    // TODO: I DONT THIN EVERY APPLIES. PATHS ARE ALWAYS ONLY ONE LEVEL. OTHERWISE THEY ARENT ENCAPSULATED
    // Multi-part path - traverse through subgraphs
    let current: INode | null = this.nodes[parts[0]] ?? null;
    for (let i = 1; i < parts.length && current; i++) {
      // Check if current is a GraphInstance with nodes
      if ('nodes' in current && typeof (current as any).nodes === 'object') {
        current = (current as GraphInstance).nodes[parts[i]] ?? null;
      } else {
        return null; // Can't traverse further
      }
    }
    return current;
  }

  // ─────────────────────────────────────────────────────────────
  // INode Implementation
  // ─────────────────────────────────────────────────────────────

  initialize(): void {
    // Preset loading is handled by Runtime - no setup needed here
  }

  resolveExposedEndpoint(endpointId: string, source) {

    // Find the internal path for this exposed endpoint
    const exposedEndpoint = this.definition.api[endpointId];
    if (!exposedEndpoint) {
      logger.error('GraphInstance', `Unknown endpoint '${endpointId}'`);
      throw new Error(`Unknown api endpoint '${endpointId}'`)
    }

    const resolved = this.resolveApiPath(exposedEndpoint.path);
    if (!resolved) {
      logger.error('GraphInstance', `Could not resolve path '${exposedEndpoint.path}' for ${source}`);
      throw new Error(`Could not resolve path '${exposedEndpoint.path}' for ${source}`)
    }

    return resolved
  }

  /**
   * Param value on an exposed endpoint.
   * Propagation to internal endpoints is handled by endpoint.subgraphMappings.
   */
  onParamChanged(payload: Payload, endpointId: string, source?: IEndpoint): void {
    // Handle preset selection - delegate to Runtime
    if (endpointId === 'preset') {
      (this.runtime as any).loadPreset(this.id, null, payload.value);
      return;
    }
    // Endpoint system handles propagation via subgraphMappings - nothing else to do
  }

  onInternalChanged(payload: Payload, endpointId: string, source: IEndpoint): void {
  }

  handleEvent(payload: Payload, endpointId: string, source: IEndpoint): void {
  }

  handleTrigger(payload: Payload, endpointId: string, source: IEndpoint): void {
  }

  onDisconnected(endpointId: string, source: IEndpoint): void {
  }

  onConnectedFrom(endpointId: string, source: IEndpoint): void {
    const resolved = this.resolveExposedEndpoint(endpointId, 'onConnectedFrom')
    this.getNodeByPath(resolved.nodeId).onConnectedFrom(resolved.endpointId, source);
  }

  onDisconnectedFrom(endpointId: string, source: IEndpoint): void {
    const resolved = this.resolveExposedEndpoint(endpointId, 'onDisconnectedFrom')
    this.getNodeByPath(resolved.nodeId).onDisconnectedFrom(resolved.endpointId, source);
  }

  onConnectedTo(endpointId: string, targetNode: INode, targetEndpoint: IEndpoint): void {
    const resolved = this.resolveExposedEndpoint(endpointId, 'onConnectedTo')
    this.getNodeByPath(resolved.nodeId).onConnectedTo(resolved.endpointId, targetNode, targetEndpoint);
  }

  onDisconnectedTo(endpointId: string, targetNode: INode, targetEndpointId: string): void {
    const resolved = this.resolveExposedEndpoint(endpointId, 'onDisconnectedTo')
    this.getNodeByPath(resolved.nodeId).onDisconnectedTo(resolved.endpointId, targetNode, targetEndpointId);
  }

  /**
   * Run animate on all child nodes, recursively into sub-graphs.
   * Each node evaluates its own expressions and pushes output.
   */
  animate(): void {
    for (const node of Object.values(this.nodes)) {
      node.animate();
    }
  }

  dispose(): void {
    for (const node of Object.values(this.nodes)) {
      node.dispose();
    }
    this.nodes = {};
    this.endpoints = {};
  }
}
